import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ProductCard from './ProductCard';
import MinisiteView from './MinisiteView';
import { markdownToExcerpt } from '../utils/text';
import { resolveMediaUrl } from '../utils/media';
import { API_BASE } from '../utils/apiBase';
import { getLifeEvent } from '../utils/lifeEvents';
import Faq from './Faq';
import Seo from './Seo';
import {
  MINISITE_OCCASIONS, MINISITE_OCCASION_KEYS, MINISITE_HUB_PATH, MINISITE_ARGUMENTS, MINISITE_VIDEO_URL,
  MINISITE_MODULES, MINISITE_SOCLE, modulesOfOccasion,
} from '../utils/minisiteOccasions';

const MODELES_PAR_ONGLET = 12;

const STYLES = [
  { key: 'tous', label: 'Tous les styles' },
  { key: 'romantique', label: 'Romantique' },
  { key: 'elegant', label: 'Élégant' },
];

// Produits « à type » (option_schema.product_type) : 'minisite' = les fiches « composition » des
// faire-part et invitations animés, 'galerie' = la galerie photos des invités. Ils n'apparaissent
// pas dans les catégories, qui ne listent que les produits ayant des visuels de carte à gratter.
export const useMinisiteProducts = () => {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/api-2026.php`, {
      params: { cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75', service: 'getProducts' },
    })
      .then((response) => setProducts((response.data?.data || []).filter((p) => p.option_schema?.product_type)))
      .catch(() => setProducts([]));
  }, []);
  return products;
};

export const isMinisite = (product) => product.option_schema?.product_type === 'minisite';
export const occasionOf = (product) => product.option_schema?.defaults?.occasion || 'mariage';

// Encart vers la galerie photos des invités (produit à part), sur les pages où il a du sens.
export const GalerieInvitesTeaser = ({ products }) => {
  const galerie = (products || []).find((p) => p.option_schema?.product_type === 'galerie');
  if (!galerie) return null;
  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, padding: 3, marginTop: 6 }}>
      <Box component="img" src={resolveMediaUrl(galerie.featured_image)} alt={galerie.titre} loading="lazy" sx={{ width: 160, borderRadius: 2 }} />
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography variant="h5" sx={{ marginBottom: 1 }}>Et après la fête ? La galerie photos des invités</Typography>
        <Typography variant="body2" sx={{ marginBottom: 2 }}>
          Un lien et un QR code : vos invités déposent leurs photos depuis leur téléphone, sans application ni compte,
          et vous récupérez tout au même endroit. Ouverte 3 mois après l'événement.
        </Typography>
        <Button variant="contained" color="secondary" component={Link} to={`/produit/${galerie.url}`}>
          Découvrir — {String(parseFloat(galerie.prix_ttc).toFixed(2)).replace('.', ',')} €
        </Button>
      </Box>
    </Paper>
  );
};

export const MinisiteProductCard = ({ product }) => (
  <ProductCard
    visuels={product.featured_image ? [{ url: product.featured_image }] : []}
    title={product.titre}
    price={parseFloat(product.prix_ttc) || 0}
    linkTo={`/produit/${product.url}`}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
      {markdownToExcerpt(product.description, 110)}
    </Typography>
    <Button variant="contained" color="secondary" sx={{ marginTop: 2 }} component={Link} to={`/produit/${product.url}`}>
      Voir la démo
    </Button>
  </ProductCard>
);

export const MinisiteArguments = () => (
  <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 1 }}>
    {MINISITE_ARGUMENTS.map((argument) => (
      <Typography component="li" variant="body2" key={argument}>✓ {argument}</Typography>
    ))}
  </Box>
);

const prixFr = (value) => `${String(parseFloat(value).toFixed(2)).replace('.', ',')} €`;

// « Ce que contient… » : le socle commun à tous les modèles, puis les modules du produit
// (minisiteModules.json — la même liste règle ce que l'éditeur ouvre après l'achat).
// `compact` (fiche produit, où le détail figure déjà dans la description) : les intitulés seulement.
export const MinisiteContenu = ({ occasionKey, titre, compact = false }) => {
  const inclus = modulesOfOccasion(occasionKey);
  const occasion = MINISITE_OCCASIONS[occasionKey];
  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Typography sx={{ fontWeight: 600, marginBottom: 1 }}>{titre || `Ce que contient votre ${occasion.produit.toLowerCase()}`}</Typography>
      <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 0.75 }}>
        {MINISITE_MODULES.filter((m) => inclus.includes(m.key)).map((m) => (
          <Typography component="li" variant="body2" key={m.key}>✓ <strong>{m.label}</strong>{compact ? '' : ` — ${m.detail}`}</Typography>
        ))}
        {!compact && MINISITE_SOCLE.map((ligne) => (
          <Typography component="li" variant="body2" color="text.secondary" key={ligne}>✓ {ligne}</Typography>
        ))}
      </Box>
    </Paper>
  );
};

// Tableau comparatif : une colonne par occasion (avec son prix), une ligne par module.
export const MinisiteComparatif = ({ products }) => {
  const colonnes = MINISITE_OCCASION_KEYS
    .map((key) => ({ key, occasion: MINISITE_OCCASIONS[key], premier: (products || []).find((p) => isMinisite(p) && occasionOf(p) === key) }))
    .filter((c) => products === null || c.premier);
  const cell = { padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.08)', textAlign: 'center', fontSize: '0.85rem' };
  return (
    <Box sx={{ overflowX: 'auto', marginBottom: 5 }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', margin: '0 auto', minWidth: 720 }}>
        <thead>
          <tr>
            <Box component="th" sx={{ ...cell, textAlign: 'left' }}>Inclus dans…</Box>
            {colonnes.map((c) => (
              <Box component="th" key={c.key} sx={{ ...cell, fontWeight: 600, lineHeight: 1.25 }}>
                <Link to={c.occasion.hub.path} style={{ color: 'inherit' }}>{c.occasion.label}</Link>
                {c.premier && <Box sx={{ fontWeight: 400, color: 'text.secondary' }}>{prixFr(c.premier.prix_ttc)}</Box>}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <Box component="td" sx={{ ...cell, textAlign: 'left' }}>Prénoms animés, polices, couleurs, lien + QR code, 24 mois en ligne</Box>
            {colonnes.map((c) => <Box component="td" key={c.key} sx={cell}>✓</Box>)}
          </tr>
          {MINISITE_MODULES.map((m) => (
            <tr key={m.key}>
              <Box component="td" sx={{ ...cell, textAlign: 'left' }} title={m.detail}>{m.label}</Box>
              {colonnes.map((c) => (
                <Box component="td" key={c.key} sx={{ ...cell, color: modulesOfOccasion(c.key).includes(m.key) ? 'inherit' : 'text.disabled' }}>
                  {modulesOfOccasion(c.key).includes(m.key) ? '✓' : '—'}
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

// Vidéo de démonstration de l'éditeur, la même partout ; rien tant qu'elle n'est pas fournie.
export const MinisiteVideo = () => (MINISITE_VIDEO_URL ? (
  <Box sx={{ maxWidth: 760, margin: '0 auto 32px' }}>
    <Typography variant="h5" sx={{ textAlign: 'center', marginBottom: 1 }}>L'éditeur en une minute</Typography>
    <video src={MINISITE_VIDEO_URL} controls preload="metadata" style={{ width: '100%', borderRadius: 12 }} />
  </Box>
) : null);

// Une tuile par occasion (image du premier modèle, nombre de modèles, prix d'appel) : page chapeau et accueil.
export const MinisiteOccasionGrid = ({ products }) => {
  const parOccasion = (key) => (products || []).filter((p) => isMinisite(p) && occasionOf(p) === key);
  return (
    <Grid container spacing={3}>
      {MINISITE_OCCASION_KEYS.map((key) => {
        const o = MINISITE_OCCASIONS[key];
        const modeles = parOccasion(key);
        const premier = modeles[0];
        if (products !== null && modeles.length === 0) return null; // occasion sans modèle en base : pas de tuile vide
        return (
          <Grid item xs={6} sm={4} md={3} key={key}>
            <Paper variant="outlined" component={Link} to={o.hub.path} sx={{ display: 'block', textDecoration: 'none', color: 'inherit', overflow: 'hidden', height: '100%' }}>
              {premier?.featured_image && (
                <Box component="img" src={resolveMediaUrl(premier.featured_image)} alt={o.hub.titre} loading="lazy" sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
              )}
              <Box sx={{ padding: 1.5 }}>
                <Typography sx={{ fontWeight: 600, lineHeight: 1.25, marginBottom: 0.5 }}>{o.hub.titre}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {products === null ? ' ' : `${modeles.length} modèle${modeles.length > 1 ? 's' : ''}`}
                  {premier ? ` · dès ${String(parseFloat(premier.prix_ttc).toFixed(2)).replace('.', ',')} €` : ''}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

// Page d'une occasion (/faire-part-mariage-anime, /invitation-bapteme-animee…) : promesse, démo dans
// le téléphone, modèles. Sans `occasionKey` : page chapeau qui présente toutes les occasions.
const MinisiteCatalogPage = ({ occasionKey }) => {
  const products = useMinisiteProducts();
  const [style, setStyle] = useState('tous');
  // Page chapeau : onglet ouvert (une occasion). `#naissance` dans l'adresse ouvre directement l'onglet.
  const [onglet, setOnglet] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return MINISITE_OCCASION_KEYS.includes(hash) ? hash : MINISITE_OCCASION_KEYS[0];
  });
  const occasion = occasionKey ? MINISITE_OCCASIONS[occasionKey] : null;
  const evenement = occasion ? getLifeEvent(occasion.hub.evenement) : null;

  const visible = useMemo(
    () => (products || []).filter((p) => isMinisite(p) && (!occasionKey || occasionOf(p) === occasionKey)
      && (style === 'tous' || p.option_schema?.style === style)),
    [products, style, occasionKey],
  );

  if (!occasion) {
    return (
      <Box sx={{ padding: { xs: 1, sm: 3 } }}>
        <Seo
          titre="Faire-part et invitations animés à partager par WhatsApp"
          description="Faire-part et invitations animés : mariage, naissance, baptême, communion, anniversaire. Un lien à partager, votre photo, réponses en ligne selon la formule."
          path={MINISITE_HUB_PATH}
        />
        <Typography variant="h1" sx={{ textAlign: 'center', marginBottom: 2 }}>Faire-part et invitations animés</Typography>
        <Typography sx={{ maxWidth: 760, margin: '0 auto 24px', textAlign: 'center' }}>
          Une invitation qui s'ouvre comme une petite page animée sur le téléphone de vos proches : le prénom apparaît
          en douceur, puis la date, votre texte, votre photo et, selon la formule, le lieu avec son plan et les réponses
          de vos invités en ligne. Vous choisissez un modèle, vous le personnalisez dans notre éditeur, vous partagez un lien.
        </Typography>
        <Paper variant="outlined" sx={{ maxWidth: 520, margin: '0 auto 32px', padding: 2 }}><MinisiteArguments /></Paper>
        <MinisiteVideo />

        {(() => {
          const parOccasion = (key) => (products || []).filter((p) => isMinisite(p) && occasionOf(p) === key);
          const ouverts = MINISITE_OCCASION_KEYS.filter((key) => products === null || parOccasion(key).length > 0);
          const courant = ouverts.includes(onglet) ? onglet : ouverts[0];
          if (!courant) return null;
          const o = MINISITE_OCCASIONS[courant];
          const modeles = parOccasion(courant);
          return (
            <>
              <Tabs
                value={courant}
                onChange={(_, v) => { setOnglet(v); window.history.replaceState(null, '', `#${v}`); }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                sx={{ marginBottom: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}
              >
                {ouverts.map((key) => (
                  <Tab key={key} value={key} label={`${MINISITE_OCCASIONS[key].label}${products ? ` (${parOccasion(key).length})` : ''}`} />
                ))}
              </Tabs>

              <Grid container spacing={3} sx={{ marginBottom: 4 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h2" component="h2" sx={{ marginBottom: 1 }}>{o.hub.titre}</Typography>
                  {modeles[0] && (
                    <Typography sx={{ fontWeight: 600, marginBottom: 1 }}>{prixFr(modeles[0].prix_ttc)} — paiement unique, invités illimités</Typography>
                  )}
                  <Typography>{o.hub.texte}</Typography>
                </Grid>
                <Grid item xs={12} md={6}><MinisiteContenu occasionKey={courant} /></Grid>
              </Grid>

              <Grid container spacing={3}>
                {modeles.slice(0, MODELES_PAR_ONGLET).map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}><MinisiteProductCard product={product} /></Grid>
                ))}
              </Grid>
              <Box sx={{ textAlign: 'center', margin: '24px 0 56px' }}>
                <Button variant="contained" color="secondary" component={Link} to={o.hub.path}>
                  {modeles.length > MODELES_PAR_ONGLET ? `Voir les ${modeles.length} modèles` : 'Voir la page'} — {o.hub.titre.toLowerCase()}
                </Button>
              </Box>
            </>
          );
        })()}

        <Typography variant="h4" component="h2" sx={{ textAlign: 'center', marginBottom: 1 }}>Comparer les formules</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginBottom: 2 }}>
          Chaque formule contient ce dont l'événement a besoin — l'éditeur ouvre exactement ce que vous avez choisi.
        </Typography>
        <MinisiteComparatif products={products} />
        <GalerieInvitesTeaser products={products} />
        <Faq page="hub" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      <Seo titre={occasion.hub.metaTitle} description={occasion.hub.metaDescription} path={occasion.hub.path} image={evenement?.image} />

      <Grid container spacing={4} alignItems="center" sx={{ marginBottom: 4 }}>
        <Grid item xs={12} md={7}>
          <Typography variant="h1" sx={{ marginBottom: 2 }}>{occasion.hub.titre}</Typography>
          <Typography sx={{ marginBottom: 2 }}>{occasion.hub.texte}</Typography>
          <MinisiteContenu occasionKey={occasionKey} />
          <Box sx={{ marginTop: 2 }}><MinisiteArguments /></Box>
          <Button component={Link} to={MINISITE_HUB_PATH} size="small" sx={{ marginTop: 2 }}>
            Voir toutes les occasions
          </Button>
        </Grid>
        <Grid item xs={12} md={5}>
          <div className="ms-phone">
            <MinisiteView embedded params={{ occasion: occasionKey, ...occasion.demo, ...occasion.hub.demo }} />
          </div>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginTop: 1 }}>
            Exemple — faites défiler dans l'écran
          </Typography>
        </Grid>
      </Grid>

      <MinisiteVideo />

      <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 2 }}>Choisissez votre modèle</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', marginBottom: 4 }}>
        {STYLES.map((s) => (
          <Chip
            key={s.key}
            label={s.label}
            clickable
            color={style === s.key ? 'secondary' : 'default'}
            variant={style === s.key ? 'filled' : 'outlined'}
            onClick={() => setStyle(s.key)}
          />
        ))}
      </Box>

      {products === null && <Typography sx={{ textAlign: 'center' }}>Chargement…</Typography>}
      {products !== null && visible.length === 0 && (
        <Typography sx={{ textAlign: 'center' }}>Aucun modèle disponible pour le moment.</Typography>
      )}
      <Grid container spacing={3}>
        {visible.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}><MinisiteProductCard product={product} /></Grid>
        ))}
      </Grid>
      {occasionKey === 'mariage' && <GalerieInvitesTeaser products={products} />}

      <Box sx={{ maxWidth: 820, margin: '56px auto 0' }}>
        {(occasion.hub.seo || []).map((bloc) => (
          <Box key={bloc.titre} sx={{ marginBottom: 4 }}>
            <Typography variant="h4" component="h2" sx={{ marginBottom: 1 }}>{bloc.titre}</Typography>
            <Typography>{bloc.texte}</Typography>
          </Box>
        ))}
        {evenement && (
          <Typography>
            À voir aussi : <Link to={evenement.path}>{evenement.titre.toLowerCase()}</Link>.
          </Typography>
        )}
      </Box>
      <Faq page={`occ-${occasionKey}`} />
    </Box>
  );
};

export default MinisiteCatalogPage;
